import * as React from 'react'

import {useFormik} from 'formik'
import invariant from 'invariant'
import {ConnectedProps} from 'react-redux'
import {useLocation} from 'react-router-dom'

import * as navigationActions from '../../actions/navigation'
import {State} from '../../reducers'

import {connect} from '../../../../commons/src/services/loop'
import * as priceApi from '../../services/api/price'
import {formDataToPriceRequest, pricingsToSingleFormData} from '../forms/pricings/utils'
import {toValidationSchema} from '../utils/form'

import {Price} from '../../types'

import chevronLeftIcon from '../../../../commons/src/assets/icons/chevron-left.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import Grid from '../../../../commons/src/components/grid'
import GridCell from '../../../../commons/src/components/grid/GridCell'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import Stack from '../../../../commons/src/components/stack/Stack'
import Container from '../../components/container'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'

import PricingsForm, {SingleFormData, initialValues, validationSchema} from '../forms/pricings/form'
import PagePartial from '../page-partial'
import SaveButtonPartial from '../save-button-partial'

import PriceCalculatorPartial from './price-calculator-partial'

const mapStateToProps = (state: State) => ({
  provider: state.core.provider,
})

const mapDispatchToProps = {
  goToPricings: navigationActions.goToPricings,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type Props = ConnectedProps<typeof connector>

const CreatePricingPage = ({provider, goToPricings}: Props) => {
  invariant(provider, 'Provider missing.')

  const routerLocation = useLocation()
  const locationState = routerLocation.state as undefined | {price: Price}
  const defaultPrice = locationState?.price

  const formik = useFormik<SingleFormData>({
    initialValues: defaultPrice
      ? {
          ...initialValues,
          ...pricingsToSingleFormData(defaultPrice),
        }
      : initialValues,
    validationSchema: toValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const priceRequest = formDataToPriceRequest(values, provider)

      try {
        await priceApi.create(priceRequest)
        goToPricings()
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  return (
    <PagePartial hideFooter>
      <PageContentLayout
        header={
          <PageHeader
            headline={
              <ActionBar>
                <Button
                  hierarchy="secondary"
                  icon={<Icon source={chevronLeftIcon} />}
                  onClick={() => goToPricings()}
                />
                <Headline size="l" label="Create new pricing" classNames={['u-no-margin-bottom']} />
              </ActionBar>
            }
          />
        }
      >
        <Container size="l">
          <Grid>
            <GridCell l={6}>
              <Stack space="300">
                <PricingsForm formik={formik} provider={provider} />
                <SaveButtonPartial formik={formik} />
              </Stack>
            </GridCell>
            <GridCell s={0} l={6}>
              <PriceCalculatorPartial
                priceRequest={formDataToPriceRequest(formik.values, provider)}
                provider={provider}
                pricingType={formik.values.pricingType}
              />
            </GridCell>
          </Grid>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default connector(CreatePricingPage)
